import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { Activity, BarChart3, Lightbulb } from "lucide-react";

interface OnboardingWelcomeProps {
  onNext: () => void;
  onSkip?: () => void;
}

export default function OnboardingWelcome({ onNext, onSkip }: OnboardingWelcomeProps) {
  const { translate: t } = useTranslation();
  return (
    <div className="text-center space-y-8">
      <div className="space-y-4">
        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
          Welcome to AlphaSignal
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
          {t("onboarding_welcome_subtitle")}
        </p>
      </div>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-12">
        <div className="p-6 rounded-lg border bg-card hover:border-primary/50 transition-colors">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Activity className="h-6 w-6 text-primary" />
          </div>
          <h3 className="font-semibold mb-2">{t("onboarding_feature_track_title")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("onboarding_feature_track_desc")}
          </p>
        </div>

        <div className="p-6 rounded-lg border bg-card hover:border-primary/50 transition-colors">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="h-6 w-6 text-primary" />
          </div>
          <h3 className="font-semibold mb-2">{t("onboarding_feature_testing_title")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("onboarding_feature_testing_desc")}
          </p>
        </div>

        <div className="p-6 rounded-lg border bg-card hover:border-primary/50 transition-colors">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Lightbulb className="h-6 w-6 text-primary" />
          </div>
          <h3 className="font-semibold mb-2">{t("onboarding_feature_ai_title")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("onboarding_feature_ai_desc")}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-4 pt-8">
        <div className="flex flex-col md:flex-row gap-3 justify-center">
          <Button size="lg" onClick={onNext} className="w-full md:w-auto px-12 h-12 text-base">
            Start Setup
          </Button>
          {onSkip && (
            <Button variant="outline" size="lg" onClick={onSkip} className="w-full md:w-auto px-8 h-12">
              Skip for Now
            </Button>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground pt-8">
        {t("onboarding_wizard_hint")}
      </p>
    </div>
  );
}
